import { getOwner } from '@/api/apiMain';
import { addressesSeparator } from '@/constants';
import { useAddressBookStore } from '@/store/addressBook';
import { useAppStore } from '@/store/app';
import { AddressInfo, PortfolioTabs } from '@/types';
import {
  getAddressSystem,
  getAddressSystemFromName,
} from '@sonarwatch/portfolio-core';
import { reactive, Reactive, ref, Ref, watch } from 'vue';

type Params = {
  rawAddresses: Ref<string[]>;
};

export default function useAddresses({ rawAddresses }: Params) {
  const appStore = useAppStore();
  const addressBookStore = useAddressBookStore();

  const isInitialized = ref(false);
  const addressesInfo: Reactive<AddressInfo[]> = reactive([]);

  watch(
    rawAddresses,
    async (newRawAddresses) => {
      appStore.portfolioTab = PortfolioTabs.portfolio;
      isInitialized.value = false;

      addressBookStore.select(newRawAddresses.join(addressesSeparator));
      addressBookStore.addRecentAddress(newRawAddresses).then();

      const promises = newRawAddresses.map(
        async (raw): Promise<AddressInfo | null> => {
          const addressSystem = getAddressSystem(raw);
          if (addressSystem) {
            // @disabled-jup : Support only Solana
            if (addressSystem !== 'solana') return null;
            return {
              address: raw,
              addressSystem: addressSystem,
            };
          }

          const addressSystemFromName = getAddressSystemFromName(raw);
          if (addressSystemFromName) {
            return getOwner(raw).then((owner) => {
              if (!owner || !owner.address) return null;
              // @disabled-jup : Support only Solana
              if (addressSystemFromName !== 'solana') return null;
              return {
                address: owner.address,
                addressSystem: addressSystemFromName,
              };
            });
          }
          return null;
        },
      );
      const result = await Promise.allSettled(promises);

      const value = result
        .map((r) => {
          if (r.status === 'rejected') return null;
          return r.value;
        })
        .filter((addressInfo) => addressInfo !== null) as AddressInfo[];

      addressesInfo.splice(0, addressesInfo.length, ...value);
      isInitialized.value = true;
    },
    {
      immediate: true,
    },
  );

  return {
    isInitialized,
    addressesInfo,
  };
}
